import React from "react"
import {
  type Placement,
  useHover,
  useInteractions,
  useFocus,
  autoUpdate,
  useFloating,
  useDismiss,
  arrow,
  flip,
  useTransitionStyles,
  offset,
  safePolygon,
  hide,
  size,
} from "@floating-ui/react"
import { FloatingPortal } from "@myvp/shared/src/components/floating/portal"
import styled from "@emotion/styled"
import { Arrow, arrowOffset } from "@myvp/shared/src/components/floating/arrow"
import useMergeRefs from "@myvp/shared/src/hooks/use-merge-refs"
import { useRTL } from "@myvp/shared/src/i18n/i18n-state-provider"

export type TooltipPlacement = Placement

export type ReferencePropGetter = (
  props?: React.HTMLProps<Element>
) => Record<string, unknown>

export interface TooltipProps {
  /**
   * className passed to the tooltip container element.
   */
  className?: string
  initialOpen?: boolean
  placement?: TooltipPlacement
  centerOffset?: boolean
  verticalOffset?: number
  /**
   * Delay in ms before showing tooltip
   */
  openDelay?: number
  matchReferenceWidth?: boolean
  /**
   * Whether or not the tooltip is open. This can be controlled or uncontrolled (pass `undefined` for uncontrolled).
   *
   * If this needs to be controlled, you should also specify a function for `onOpenChange`.
   */
  open?: boolean
  /**
   * Called when the open status of the tooltip should change. This can be controlled or uncontrolled (pass `undefined` for uncontrolled).
   *
   * The `open` prop should also be provided if a function is passed for this prop.
   */
  onOpenChange?: (open: boolean) => void
  /**
   * Called when the tooltip has finished its close transition and unmounted.
   */
  onDismissed?: () => void
  /**
   * Whether or not the tooltip will be displayed when the reference element is hovered.
   *
   * This is useful for cases where a tooltip should only be available when certain conditions are met
   * and the open state doesn't need to be fully controlled.
   *
   * @default true
   */
  active?: boolean
  variant?: "error"
  /**
   * Content shown in the tooltip when it's visible.
   */
  content: React.ReactNode
  referenceRef?: React.Ref<Element>
  /**
   * UI that the tooltip is anchored too.
   *
   * If this is a render prop, it's passed a props getter function.
   *
   * If this is not a render prop, React.cloneElement is used to pass the result of the props getter function.
   *
   * @example
   * With the render prop:
   * ```tsx
   * <Tooltip {...otherProps}>
   *   {(getProps) => <Anchor {...getProps()} />}
   * </Tooltip>
   * ```
   *
   * Which is equivalent to:
   * ```tsx
   * <Tooltip {...otherProps}>
   *   <Anchor />
   * </Tooltip>
   * ```
   */
  children:
    | React.ReactElement
    | ((getProps: ReferencePropGetter) => React.ReactNode)
}

const Tooltip = (props: TooltipProps) => {
  const active = props.active ?? true
  const [internalOpen, setInternalOpen] = React.useState(!!props.initialOpen)

  // these can be controlled by the user of this component or uncontrolled
  const open = active && (props.open ?? internalOpen)
  const setOpen = props.onOpenChange ?? setInternalOpen

  const transitionDelay = Math.max(props.openDelay ?? 0, 0) / 1000

  const tooltipId = React.useId()

  const arrowRef = React.useRef<SVGSVGElement>(null)

  const isRtl = useRTL()
  let placement = props.placement

  if (isRtl && placement) {
    placement = placement.replace(/left|right/, (match) =>
      match === "left" ? "right" : "left"
    ) as TooltipPlacement
  }

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [
      arrowOffset(),
      props.centerOffset
        ? offset((state) => -state.rects.reference.height / 2)
        : offset(3),
      offset(props.verticalOffset),
      flip({ fallbackAxisSideDirection: "start" }),
      arrow({ element: arrowRef }),
      props.matchReferenceWidth &&
        size({
          apply: (state) => {
            Object.assign(state.elements.floating.style, {
              width: `${state.rects.reference.width}px`,
            })
          },
        }),
      // including shift to avoid going off the viewport (during scrolling)
      hide({
        strategy: "referenceHidden",
      }),
    ],
    whileElementsMounted: (...args) => {
      // by default, useFloating only updates position when this component rerenders.
      // autoupdate fixes that for interactions that don't cause a rerender, like scrolling.
      const cleanup = autoUpdate(...args, { animationFrame: false })
      return cleanup
    },
  })

  // if the open state is controlled, but there is no `onOpenChange` handler,
  // then none of the floating-ui interactions need to be used (they won't work properly anyway).
  // This is useful for cases where a tooltip is shown on a timer or doesn't need these interactions.
  const disableInteractions =
    typeof props.open === "boolean" && !props.onOpenChange
  // a11y: reference for below behavior: https://www.w3.org/WAI/ARIA/apg/patterns/tooltip/
  // a11y: open on reference hover
  const hover = useHover(data.context, {
    enabled: active && !disableInteractions,
    move: false,
    // keep the tooltip open when the tooltip is hovered
    handleClose: safePolygon(),
  })
  // a11y: open the tooltip if the reference element is focused
  const focus = useFocus(data.context, {
    enabled: active && !disableInteractions,
  })
  // a11y: dismiss the tooltip when the Esc key is pressed
  const dismiss = useDismiss(data.context, {
    enabled: active && !disableInteractions,
    outsidePress: false,
  })

  const interactions = useInteractions([hover, focus, dismiss])

  const { isMounted, styles: transitionStyles } = useTransitionStyles(
    data.context,
    { duration: 500, initial: { opacity: 0 } }
  )
  {
    const prevIsMounted = React.useRef(isMounted)
    React.useEffect(() => {
      if (prevIsMounted.current && !isMounted) {
        const onDismissed = props.onDismissed
        onDismissed?.()
      }
      prevIsMounted.current = isMounted
    }, [isMounted, props.onDismissed])
  }

  const mergedRef = useMergeRefs([data.refs.setReference, props.referenceRef])

  const getReferenceProps: ReferencePropGetter = (externalProps) =>
    interactions.getReferenceProps({
      ref: active ? mergedRef : props.referenceRef,
      "aria-describedby": open ? tooltipId : undefined,
      ...externalProps,
    })

  return (
    <>
      {typeof props.children === "function"
        ? props.children(getReferenceProps)
        : React.cloneElement(props.children, getReferenceProps())}
      {isMounted && props.content && (
        <FloatingPortal>
          <BaseTooltip
            id={tooltipId}
            ref={data.refs.setFloating}
            className={props.className}
            variant={props.variant}
            style={{
              ...data.floatingStyles,
              ...transitionStyles,
              transitionDelay: transitionDelay
                ? `${transitionDelay}s`
                : undefined,
              visibility: data.middlewareData.hide?.referenceHidden
                ? "hidden"
                : "visible",
            }}
            {...interactions.getFloatingProps()}
            role="tooltip"
          >
            <Arrow ref={arrowRef} context={data.context} />
            {props.content}
          </BaseTooltip>
        </FloatingPortal>
      )}
    </>
  )
}

export default styled(Tooltip)``

export const BaseTooltip = styled.div<{ variant?: TooltipProps["variant"] }>`
  --floating-bg: ${(props) =>
    props.variant === "error"
      ? props.theme.palette.content.critical.default
      : props.theme.palette.surface.tooltip.default};
  background: var(--floating-bg);
  color: ${(props) => props.theme.palette.content.neutral.inverse};
  border-radius: 4px;
  box-shadow: 0px 8px 8px rgba(0, 0, 0, 0.15);
  text-align: center;
  padding: 8px;
  z-index: ${(props) => props.theme.zIndex.tooltip};

  a {
    color: ${(props) => props.theme.palette.content.link.default};
    text-decoration: none;
  }
`
